import { apiClient } from "../services/apiClient";
import { ATP_API_URL } from "../config/apiConfig";

// ── Normalizers ────────────────────────────────────────────────────────────────
const normalizeCategory = (v) => {
  if (!v) return "";
  const m = { client: "client", "service partner": "Service Partner" };
  return m[v.toLowerCase().trim()] ?? v;
};

const normalizeLOB = (v) => {
  if (!v) return "";
  const m = {
    travel: "travel",
    health: "health",
    auto: "auto",
    gadget: "gadget",
    company: "company",
    corporate: "corporate",
    "non-travel": "company",
    // service variants — keep as-is for dept map lookup
    travel_service: "travel_service",
    auto_service: "auto_service",
    gadget_service: "gadget_service",
    mobile_service: "mobile_service",
  };
  return m[v.toLowerCase().trim()] ?? v.toLowerCase();
};

const normalizeEntityType = (v) => {
  if (!v) return "";
  const m = {
    proprietor: "proprietorship",
    proprietorship: "proprietorship",
    partnership: "partnership",
    llp: "llp",
    "private limited": "private_limited",
    private_limited: "private_limited",
    "pvt ltd": "private_limited",
    "pvt. ltd": "private_limited",
    "public limited": "public_limited",
    public_limited: "public_limited",
    trust: "trust",
    society: "society",
  };
  return m[v.toLowerCase().trim()] ?? v;
};

const normalizeAccountType = (v) => {
  if (!v) return "";
  const m = { saving: "savings", savings: "savings", current: "current" };
  return m[v.toLowerCase().trim()] ?? v.toLowerCase();
};

const normalizeDept = (v) => {
  if (!v) return "";
  // Capitalize first letter of each word to match dropdown option values
  return v.replace(/\b\w/g, (c) => c.toUpperCase());
};

// ── Map flat API record → { entity, documents } ────────────────────────────────
const mapRecord = (r) => {
  // Build documents array from *Path fields
  const docPathMap = [
    { key: "doc_aadhar", label: "Aadhar Card", path: r.aadharCardPath },
    { key: "doc_pan", label: "PAN Card", path: r.panCardPath },
    {
      key: "doc_partnershipDeed",
      label: "Partnership Deed",
      path: r.partnershipDeedPath,
    },
    {
      key: "doc_aadharBoth",
      label: "Aadhar of Both Partners",
      path: r.aadharOfBothPartnersPath,
    },
    {
      key: "doc_coi",
      label: "Certificate of Incorporation",
      path: r.certificateOfIncorporationPath,
    },
    { key: "doc_directorPan", label: "Director PAN", path: r.directorPANPath },
    {
      key: "doc_directorAadhar",
      label: "Director Aadhar",
      path: r.directorAadharPath || "",
    },
    {
      key: "doc_llpCertificate",
      label: "LLP Certificate",
      path: r.uploadLLPCertificatePath || "",
    },
    {
      key: "doc_msme_certificate",
      label: "MSME Certificate",
      path: r.msmeCertificatePath,
    },
    {
      key: "doc_utility_bill",
      label: "Utility Bill",
      path: r.utilityBillDocumentPath,
    },
    {
      key: "doc_bank_proof",
      label: "Passbook / Cancelled Cheque",
      path: r.passbookOrCancelledChequePath,
    },
    {
      key: "third_party_file",
      label: "Signed Form (Third Party)",
      path: r.uploadSignedFormPath,
    },
  ];

  const documents = docPathMap
    .filter((d) => d.path && d.path !== "string")
    .map((d) => {
      console.log(`[mapRecord] doc key=${d.key} path=${d.path}`);
      return {
        key: d.key,
        documentName: d.label,
        files: [
          {
            isUploaded: true,
            fileName: d.path.split("\\").pop() || d.label,
            filePath: d.path,
          },
        ],
      };
    });

  (r.gstList || []).forEach((g, i) => {
    const certPath =
      g.gstCertificatePath && g.gstCertificatePath !== "string"
        ? g.gstCertificatePath
        : null;
    documents.push({
      key: `gst_file_${i}`,
      documentName: `GST Certificate — ${g.gstNumber || i + 1}`,
      files: certPath
        ? [
            {
              isUploaded: true,
              fileName: certPath.split("\\").pop() || "GST",
              filePath: certPath,
            },
          ]
        : [],
    });
  });

  const mapped = {
    entity: {
      id: r.id,
      entityId: r.entityId || "",
      entityPrimaryId: r.entityPrimaryId,
      kycId: r.kycId,
      parentId: r.parentId,
      parentCompanyName: r.parentName || "",
      // Primary
      entityName: r.entityName || "",
      EntityName: r.entityName || "",
      entityCategory: normalizeCategory(r.entityCategory),
      entityLOBType: normalizeLOB(r.entityLOBType),
      lobType: normalizeLOB(r.entityLOBType),
      aaDepartmentFunction: normalizeDept(r.aaDepartmentFunction),
      emailAddress: r.emailAddress || "",
      contactPersonEmail: r.emailAddress || "",
      spocEmailAddress: r.spoC_EmailAddress || "",
      requireKYC: r.requireKYC,
      activeLink: r.activeLink,
      kyC_URL: r.kyC_URL || "",
      isApproved: r.isVerified || false,
      isVerified: r.isVerified || false,
      createdBy: r.createdBy || "",
      createdOn: r.createdOn || "",
      modifiedOn: r.modifiedOn || "",
      // Timeline dates
      kycCreatedOn: r.kycId ? r.modifiedOn || "" : "", // when KYC was submitted
      docsUploadedOn: r.kycCreatedOn ? r.kycCreatedOn || "" : "",
      verifiedOn: r.isVerified ? r.isVerified || "" : "",
      // KYC entity
      parentName: r.entityName || "",
      tradeName: r.tradeName || "",
      pinCode: r.pinCode || "",
      city: r.city || "",
      State: r.state || "",
      state: r.state || "",
      address: r.address || "",
      natureOfBusiness: r.natureOfBusiness || "",
      invoiceRequired: r.invoiceRequired ?? null,
      isInvoiceRequired:
        r.invoiceRequired === true
          ? "yes"
          : r.invoiceRequired === false
            ? "no"
            : "",
      entityType: normalizeEntityType(r.entityType),
      CompanypanNumber: r.panCardNumber || "",
      // Contact
      contactPersonName: r.contactPersonName || "",
      contactPersonDesignation: r.designation || "",
      contactPersonMobile: r.mobileNumber || "",
      contactEmail: r.contactPersonEmail || r.emailAddress || "",
      // MSME
      isRegisteredUnderMSME: r.isRegisterdUnderMSME,
      isMSMERegistered:
        r.isRegisterdUnderMSME === true
          ? "yes"
          : r.isRegisterdUnderMSME === false
            ? "no"
            : "",
      msmeNumber: r.msmeRegistrationNumber || "",
      msmeRegistrationNumber: r.msmeRegistrationNumber || "",
      // Document numbers
      panCardNumber: r.panCardNumber || "",
      aadharCardNumber: r.aadharCardNumber || "",
      llpCrtificateNumber: r.llpCrtificateNumber || "",
      partnershipDeedNumber: r.partnershipDeedNumber || "",
      aadharNumberOfBothPartners: r.aadharNumberOfBothPartners || "",
      certificateOfIncorporationNumber:
        r.certificateOfIncorporationNumber || "",
      directorPANNumber: r.directorPANNumber || "",
      directorAadharNumber: r.directorAadharNumber || "",
      trustDeedNumber: r.trustDeedNumber || "",
      panOfTrustNumber: r.panOfTrustNumber || "",
      aadharNumberOfTrustee: r.aadharNumberOfTrustee || "",
      panOfTrusteeNumber: r.panOfTrusteeNumber || "",
      // GST
      isGSTRegistered:
        r.isGSTRegistered === true
          ? "yes"
          : r.isGSTRegistered === false
            ? "no"
            : "",
      gstList: r.gstList || [],
      utilityType: r.utilityType || "",
      utilityProviderName: r.providerName || "",
      utilityAccountNumber: r.account_ConsumerNumber || "",
      utilityAddress: r.addressOnUtilityBill || "",
      // keep original names too for backward compat
      providerName: r.providerName || "",
      accountOrConsumerNumber: r.account_ConsumerNumber || "",
      addressOnUtilityBill: r.addressOnUtilityBill || "",
      // Bank
      hasBankDetails:
        r.provideBankDetails === true
          ? "yes"
          : r.provideBankDetails === false
            ? "no"
            : "",
      bankName: r.bankName || "",
      accountNumber: r.accountNumber || "",
      ifscCode: r.ifscCode || "",
      accountType: normalizeAccountType(r.accountType),
      branchName: r.branchName || "",
      beneficiaryName: r.beneficiaryName || "",
      relationWithBeneficiary: r.relationWithBeneficiary || "",
      // Paths
      aadharCardPath: r.aadharCardPath || "",
      panCardPath: r.panCardPath || "",
      partnershipDeedPath: r.partnershipDeedPath || "",
      uploadLLPCertificatePath: r.uploadLLPCertificatePath || "",
      aadharOfBothPartnersPath: r.aadharOfBothPartnersPath || "",
      certificateOfIncorporationPath: r.certificateOfIncorporationPath || "",
      directorPANPath: r.directorPANPath || "",
      directorAadharPath: r.directorAadharPath || "",
      msmeCertificatePath: r.msmeCertificatePath || "",
      utilityBillDocumentPath: r.utilityBillDocumentPath || "",
      passbookOrCancelledChequePath: r.passbookOrCancelledChequePath || "",
      uploadSignedFormPath: r.uploadSignedFormPath || "",
      isDocumentUploaded: r.isDocumentUploaded,
    },
    documents,
  };

  // Debug: log mapped entity for records with requireKYC true
  if (r.requireKYC) {
    console.log(`[mapRecord] id=${r.id} requireKYC=true →`, {
      parentName: mapped.entity.parentName,
      tradeName: mapped.entity.tradeName,
      entityType: mapped.entity.entityType,
      isGSTRegistered: mapped.entity.isGSTRegistered,
      isMSMERegistered: mapped.entity.isMSMERegistered,
      hasBankDetails: mapped.entity.hasBankDetails,
    });
  }
  return mapped;
};

// ── Action ─────────────────────────────────────────────────────────────────────
export const fetchEntityListAction = async (
  pageNumber = 1,
  pageSize = 10,
  filters = {},
) => {
  try {
    const {
      entityName,
      emailAddress,
      entityCategory,
      status,
      isVerified,
      lobType,
      dateFrom,
      dateTo,
    } = filters;

    const body = {
      id: null,
      category: entityCategory || null,
      entityName: entityName || null,
      email: emailAddress || null,
      documentStatus:
        status === "Done" ? true : status === "pending" ? false : null,
      isVerified:
        isVerified === true ? true : isVerified === false ? false : null,
      entityLOBType: lobType || null,
      startDate: dateFrom ? new Date(dateFrom).toISOString() : null,
      endDate: dateTo
        ? new Date(new Date(dateTo).setHours(23, 59, 59, 999)).toISOString()
        : null,
      pageNumber,
      pageSize,
    };

    const response = await apiClient.post(
      ATP_API_URL.GET_ALL_DATA_SEARCH,
      body,
    );
    console.log("[EntityList] raw response:", response?.data);

    if (response?.status === 200 && response.data) {
      const rawList = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      // API returns all matching records — use totalRecords if provided, else rawList.length
      const totalRecords =
        response.data?.totalRecords ??
        response.data?.pagination?.totalItems ??
        rawList.length;

      const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

      // Client-side slice if API doesn't paginate server-side
      const isServerPaginated =
        response.data?.totalRecords != null ||
        response.data?.pagination != null;
      const pageData = isServerPaginated
        ? rawList.map(mapRecord)
        : rawList
            .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
            .map(mapRecord);

      return {
        success: true,
        data: pageData,
        pagination: {
          currentPage: pageNumber,
          totalPages,
          totalItems: totalRecords,
          pageSize,
          hasNextPage: pageNumber < totalPages,
          hasPreviousPage: pageNumber > 1,
        },
      };
    }

    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      message: "Failed to fetch entity list",
    };
  } catch (error) {
    console.error("[EntityList] error:", error.message);
    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      message: error.message,
      error,
    };
  }
};
